/**
 * portalClickGuard.ts — Round O (2026-07-15) guest sign-in link fix (O2).
 *
 * Several chat surfaces render OUTSIDE the chat container via
 * `ReactDOM.createPortal(..., document.body)` (token-usage ⓘ popups, special
 * tile popups). Document-level mousedown handlers that treat "not inside the
 * chat container" as "outside" would otherwise:
 *   1. close the whole chat pane (useGlobalChat handleClickOutside), and/or
 *   2. unmount the popup before the browser can complete the click
 *      (GlobalChatSystem handleGlobalClick) — which killed the guest
 *      "Sign in" anchor inside the portaled ⓘ popup: mousedown removed the
 *      anchor from the DOM, so the click (and its default /auth navigation)
 *      never happened.
 *
 * Portaled chat surfaces mark their root with `data-prest0n-portal="true"`.
 * This module is the single shared predicate both handlers consult, so the
 * two mousedown paths can never drift apart again.
 */

export const PREST0N_PORTAL_SELECTOR = '[data-prest0n-portal="true"]';

/**
 * True when the event target sits inside a body-portaled chat surface.
 * Accepts any EventTarget: Elements match via `closest`; non-Element nodes
 * (e.g. text nodes) walk to their parent element first; everything else
 * (window/document/null) is not inside a portal.
 */
export function isInsidePrest0nPortal(target: EventTarget | null): boolean {
  if (!target) return false;
  if (target instanceof Element) {
    return !!target.closest(PREST0N_PORTAL_SELECTOR);
  }
  if (target instanceof Node && target.parentElement) {
    return !!target.parentElement.closest(PREST0N_PORTAL_SELECTOR);
  }
  return false;
}
